import React, { useState, useEffect, useRef } from 'react';

const BACKEND = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * WaitlistModal
 * Props:
 *   isOpen   — boolean
 *   plan     — 'Pro' | 'Teams'
 *   onClose  — () => void
 *   onSuccess — () => void   (called after email is saved, used to show toast)
 */
export default function WaitlistModal({ isOpen, plan, onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Focus email field when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setError('');
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');

    try {
      // ── API call ────────────────────────────────────────────────────────────
      // When migrating to Supabase, replace this block with:
      //   const { error } = await supabase.from('waitlist').insert({ email, plan, source: 'landing-page' });
      //   if (error) throw new Error(error.message);
      // ────────────────────────────────────────────────────────────────────────
      const res = await fetch(`${BACKEND}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          plan,
          source: 'landing-page-painted-door',
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');

      onClose();
      onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="wl-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={`Join ${plan} waitlist`}
    >
      <div className="wl-modal">

        {/* Close */}
        <button className="wl-close" onClick={onClose} aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Icon */}
        <div className="wl-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
          </svg>
        </div>

        {/* Copy */}
        <h2 className="wl-heading">
          {plan} &amp; Teams — Coming Soon!
        </h2>
        <p className="wl-body">
          We&rsquo;re putting the final polish on our premium features.
          Drop your email below and we&rsquo;ll give you{' '}
          <strong>50% off your first month</strong> when we launch.
        </p>

        {/* Form */}
        <form className="wl-form" onSubmit={handleSubmit} noValidate>
          <div className="wl-input-wrap">
            <svg className="wl-input-icon" xmlns="http://www.w3.org/2000/svg" width="15" height="15"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M2 7l10 7 10-7" />
            </svg>
            <input
              ref={inputRef}
              id="wl-email-input"
              className="wl-input"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="you@example.com"
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          {error && <p className="wl-error">{error}</p>}

          <button
            type="submit"
            className="wl-submit"
            disabled={loading || !email.trim()}
          >
            {loading ? (
              <><span className="wl-spinner" /> Saving your spot…</>
            ) : (
              'Join Waitlist'
            )}
          </button>
        </form>

        <p className="wl-fine-print">No spam. Unsubscribe at any time.</p>

      </div>
    </div>
  );
}
